import React, { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ReactPlaceholder from 'react-placeholder';
import { AppBundle } from 'api/optimalprint-sdk.d';
import getPrice from 'store/price/selector/getPrice';
import getCurrencyId from 'store/price/selector/getCurrencyId';
import getPriceFormat from 'store/price/selector/getPriceFormat';
import { Store } from 'store';
import cn from 'classnames';
import formatPrice from 'utils/price/formatPrice';
import PriceComponent from 'components/Common/Prices/ProductPrice/PriceComponent';
import PricesSize from 'components/Common/Prices/ProductPrice/types';
import styles from './MinimalProductPrice.module.scss';

export interface MinimalProductPriceProps {
  categoryId: number;
  quantity: number;
  product?: AppBundle.Api.Entity.App.V1.IAbstractProduct;
  priceUnits?: ReactElement;
  theme?: string;
  size?: PricesSize;
}

const MinimalProductPrice: FC<MinimalProductPriceProps> = ({ categoryId, quantity, product, theme, size }) => {
  const { t } = useTranslation();
  const currencyId = useSelector(getCurrencyId);
  const priceFormat = useSelector(getPriceFormat);
  const productPrice = useSelector((store: Store) => (pId: number) => getPrice(store, categoryId, pId, quantity));
  let allProductIds = product?.products?.map((productItem) => productItem.productId) || [];
  allProductIds = [...new Set(allProductIds)];
  const getAllPrices = allProductIds.map((pId) => productPrice(pId)).filter((pId) => pId);
  getAllPrices.sort((a: any, b: any) => a.p - b.p);

  const price = getAllPrices[0];
  const isDataReady = Boolean(price && priceFormat && currencyId);
  const hasDiscount = price && price.pi !== price.p;

  if (!price) {
    return null;
  }

  const regularPrice = currencyId && formatPrice(price.pi, priceFormat || '', currencyId);
  const discountedPrice = currencyId && formatPrice(price.p, priceFormat || '', currencyId);

  return (
    <div className={styles.minimalProductPrice}>
      <ReactPlaceholder
        showLoadingAnimation
        type="textRow"
        ready={isDataReady}
        style={{ width: '100px', backgroundColor: '#e8e8e8' }}
      >
        <span className={cn(styles[`theme-${theme}`])}>{t('txt_from')}</span>
        <PriceComponent
          size={size}
          initialPrice={price.pi}
          initialPriceFormatted={regularPrice}
          hasDiscount={hasDiscount}
          finalPrice={price.p}
          finalPriceFormatted={discountedPrice}
          theme={theme}
        />
      </ReactPlaceholder>
    </div>
  );
};

export default MinimalProductPrice;
